<template>
  <v-card
    class="ads-card elevation-8"
    :class="{ 'ads-card--hovered': isHovered }"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
  >
    <!-- Premium gradient overlay -->
    <div class="premium-overlay"></div>

    <!-- Action buttons with floating animation -->
    <div class="action-buttons" :class="{ visible: isHovered }">
      <v-btn
        fab
        small
        class="action-btn edit-btn"
        @click="onEditAds"
        elevation="4"
      >
        <v-icon color="white" size="18">mdi-pencil</v-icon>
      </v-btn>
      <v-btn
        fab
        small
        class="action-btn delete-btn"
        @click="onDeleteAds"
        elevation="4"
      >
        <v-icon color="white" size="18">mdi-delete</v-icon>
      </v-btn>
    </div>

    <div class="ads-card-content" :class="getContentClasses">
      <!-- Media section with enhanced styling -->
      <div class="media-container">
        <div class="media-wrapper" v-if="ads.type == 'image'">
          <v-img
            :src="ads.file"
            alt="ads logo"
            class="ads-media rounded-xl"
            gradient="to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.1) 100%"
          >
            <div class="media-type-badge">
              <v-icon small color="white">mdi-image</v-icon>
            </div>
          </v-img>
        </div>

        <div
          v-else-if="ads.type == 'video' && ads.file"
          class="media-wrapper video-wrapper"
        >
          <vue-plyr
            ref="plyr"
            :options="{
              autoplay: false,
              muted: true,
              autopause: false,
              controls: [
                'play-large',
                'play',
                'progress',
                'current-time',
                'mute',
                'volume',
                'fullscreen'
              ]
            }"
            class="custom-plyr"
          >
            <video
              controls
              crossorigin
              playsinline
              id="videoElement"
              :settings="['loop']"
              loop
              class="rounded-xl"
            >
              <source :src="ads.file" type="video/mp4" />
            </video>
          </vue-plyr>
          <div class="media-type-badge">
            <v-icon small color="white">mdi-play</v-icon>
          </div>
        </div>
      </div>

      <!-- Content section with enhanced typography -->
      <div class="content-section">
        <div class="url-container"  @click="goToWebsite()">
          <v-chip small outlined class="url-chip" color="primary">
            <v-icon left x-small>mdi-link</v-icon>
            {{ truncateUrl(ads.url) }}
          </v-chip>
        </div>

        <div class="description-container">
          <p class="ads-description">
            {{ ads.text }}
          </p>
        </div>

        <div class="business-info">
          <div class="business-name">
            <v-icon small class="business-icon">mdi-domain</v-icon>
            {{ ads.business_name }}
          </div>

          <!-- Premium status indicator -->
          <div class="status-indicator">
            <div class="pulse-dot"></div>
            <span class="status-text">Active</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Dialogs remain the same -->
    <admin-edit-ads-dialog
      :onClose="onCloseDialog"
      :ads="ads"
      :dialog="editDialog"
    />
    <confirm-delete-dialog
      :deleteDialog="deleteDialog"
      :onClose="onCloseDialog"
      :onConfirm="onConfirmDelete"
    />
  </v-card>
</template>

<script>
import { mapActions } from "vuex";
import AdminEditAdsDialog from "../../views/admin/ads/AdminEditAdsDialog.vue";
import ConfirmDeleteDialog from "../common/ConfirmDeleteDialog.vue";

export default {
  components: { AdminEditAdsDialog, ConfirmDeleteDialog },
  props: {
    ads: Object
  },
  data() {
    return {
      editDialog: false,
      deleteDialog: false,
      isHovered: false
    };
  },
  computed: {
    getContentClasses() {
      // More granular breakpoint logic
      if (this.$vuetify.breakpoint.lgAndDown) {
        return "content-vertical";
      }
      return "content-horizontal";
    }
  },
  methods: {
    ...mapActions("ads", {
      editAds: "editAds",
      deleteAds: "deleteAds"
    }),
    onEditAds() {
      this.editDialog = true;
    },
    onDeleteAds() {
      this.deleteDialog = true;
    },
    onCloseDialog() {
      this.editDialog = false;
      this.deleteDialog = false;
    },
    onConfirmDelete() {
      this.deleteAds({ _id: this.ads._id }).catch(error => {
        console.log(error.response.data.message);
      });
    },
    // gotToRoute(url) {
    //   if (url) {
    //     // Only open if the URL is not from our own domain
    //     const link = document.createElement('a');
    //     link.href = url;
    //     const isSameDomain = link.hostname === window.location.hostname;
    //     if (!isSameDomain) {
    //       window.open(url, "_blank");
    //     }
    //   }
    // },
    goToWebsite() {
      const url = this.ads.url.startsWith("http")
        ? this.ads.url
        : `http://${this.ads.url}`;
      window.open(url);
    },
    truncateUrl(url) {
      if (!url) return "";
      return url.length > 25 ? url.substring(0, 25) + "..." : url;
    }
  }
};
</script>

<style scoped>
.ads-card {
  position: relative;
  border-radius: 20px !important;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%);
  border: 1px solid rgba(71, 37, 131, 0.1);
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  overflow: hidden;
  backdrop-filter: blur(10px);
  min-height: 200px;
}

.ads-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px rgba(71, 37, 131, 0.15) !important;
}

.ads-card--hovered {
  border-color: rgba(71, 37, 131, 0.3);
}

.premium-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #472583, #6c5ce7, #74b9ff, #00cec9);
  background-size: 300% 100%;
  animation: shimmer 3s ease-in-out infinite;
}

@keyframes shimmer {
  0%,
  100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

.action-buttons {
  position: absolute;
  top: 15px;
  right: 15px;
  display: flex;
  gap: 8px;
  opacity: 0;
  transform: translateY(-10px);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  z-index: 10;
}

.action-buttons.visible {
  opacity: 1;
  transform: translateY(0);
}

.action-btn {
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.edit-btn {
  background: linear-gradient(135deg, #6c5ce7, #74b9ff) !important;
}

.delete-btn {
  background: linear-gradient(135deg, #fd79a8, #e84393) !important;
}

.action-btn:hover {
  transform: scale(1.1);
}

.ads-card-content {
  display: flex;
  padding: 24px;
  gap: 20px;
  align-items: flex-start;
  min-height: 180px;
  width: 100%;
  box-sizing: border-box;
}

.content-horizontal {
  flex-direction: row;
}

.content-horizontal .media-wrapper {
  flex-shrink: 0;
  width: 280px;
  min-width: 200px;
}

.content-horizontal .content-section {
  flex: 1;
  min-width: 0; /* Important: allows content to shrink */
  overflow: hidden;
}

.content-vertical {
  flex-direction: column;
  align-items: stretch;
}

.content-vertical .media-container {
  align-self: center;
}

.content-vertical .media-wrapper {
  width: 100%;
  max-width: 500px;
}

.media-container {
  flex-shrink: 0;
}

.media-wrapper {
  position: relative;
  width: 280px;
  height: 160px;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
}

.media-wrapper:hover {
  transform: scale(1.02);
  box-shadow: 0 12px 35px rgba(0, 0, 0, 0.2);
}

.ads-media {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.video-wrapper {
  background: #000;
}

.media-type-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 6px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.content-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 8px 0;
  min-width: 0;
  overflow: hidden;
}

.url-container {
  display: flex;
  align-items: center;
  width: 100%;
}

.url-chip {
  font-weight: 600;
  font-size: 12px;
  background: rgba(71, 37, 131, 0.1) !important;
  border-color: rgba(71, 37, 131, 0.3) !important;
  transition: all 0.3s ease;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
}

.url-chip:hover {
  background: rgba(71, 37, 131, 0.2) !important;
  transform: translateX(4px);
}

.description-container {
  flex: 1;
  width: 100%;
  overflow: hidden;
}

.ads-description {
  color: #2d3748;
  font-size: 15px;
  line-height: 1.6;
  margin: 0;
  font-weight: 400;
  letter-spacing: 0.3px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-wrap: break-word;
  hyphens: auto;
}

.business-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 12px;
  border-top: 1px solid rgba(71, 37, 131, 0.1);
  gap: 8px;
  flex-wrap: wrap;
}

.business-name {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #472583;
  font-weight: 600;
  font-size: 14px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}

.business-icon {
  color: #6c5ce7;
}

.status-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pulse-dot {
  width: 8px;
  height: 8px;
  background: #00cec9;
  border-radius: 50%;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(0, 206, 201, 0.7);
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 10px rgba(0, 206, 201, 0);
  }
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(0, 206, 201, 0);
  }
}

.status-text {
  font-size: 12px;
  color: #00cec9;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Custom Plyr styling */
.custom-plyr :deep(.plyr) {
  border-radius: 16px;
}

.custom-plyr :deep(.plyr__video-wrapper) {
  padding-top: 57.14%;
  border-radius: 16px;
  overflow: hidden;
}

.custom-plyr :deep(.plyr video) {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  border-radius: 16px;
}

.custom-plyr :deep(.plyr__controls) {
  background: linear-gradient(0deg, rgba(0, 0, 0, 0.8) 0%, transparent 100%);
  border-radius: 0 0 16px 16px;
}

/* Responsive design */

/* Extra large screens - horizontal layout only for very wide screens */
@media (min-width: 1904px) {
  .content-horizontal .media-wrapper {
    width: 320px;
  }
}

/* Large screens and below - switch to vertical layout */
@media (max-width: 1903px) {
  .ads-card-content {
    flex-direction: column !important;
    gap: 16px;
    align-items: stretch;
  }
  
  .media-container {
    align-self: center;
  }
  
  .media-wrapper {
    width: 100% !important;
    max-width: 500px !important;
    min-width: auto !important;
  }
  
  .content-section {
    min-width: auto;
  }
}

/* Medium screens */
@media (max-width: 1264px) {
  .ads-card-content {
    padding: 20px;
  }
  
  .media-wrapper {
    max-width: 450px !important;
  }
}

/* Small-medium screens */
@media (max-width: 960px) {
  .ads-card-content {
    padding: 18px;
  }
  
  .media-wrapper {
    max-width: 400px !important;
    height: 150px;
  }
}

/* Small screens */
@media (max-width: 768px) {
  .ads-card-content {
    padding: 16px;
  }
  
  .media-wrapper {
    height: 140px;
    max-width: 350px !important;
  }
  
  .action-buttons {
    opacity: 1;
    transform: translateY(0);
  }
  
  .content-section {
    gap: 12px;
  }
  
  .ads-description {
    font-size: 14px;
  }
  
  .business-info {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
}

/* Extra small screens */
@media (max-width: 480px) {
  .ads-card-content {
    padding: 12px;
  }
  
  .media-wrapper {
    height: 120px;
    max-width: 100% !important;
  }
  
  .ads-description {
    font-size: 13px;
    -webkit-line-clamp: 2;
    line-clamp: 2;
  }
  
  .business-name {
    font-size: 13px;
  }
  
  .status-text {
    font-size: 11px;
  }
}
</style>
